import { useCallback, useMemo, useState } from "react";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";

import type { ProjectEntry } from "@taskin-code/contracts";

import { projectListDirectoryQueryOptions } from "~/lib/projectReactQuery";
import { useEditorStore } from "~/editor/editorStore";
import { useTheme } from "~/hooks/useTheme";
import { VscodeEntryIcon } from "../chat/VscodeEntryIcon";

/**
 * The tree loads lazily: the root when it mounts, and every other directory
 * only while it is expanded. Entries arrive already sorted from the server
 * (directories first, then alphabetical), so rows render in listing order.
 */

function basenameOf(entryPath: string): string {
  const separatorIndex = entryPath.lastIndexOf("/");
  return separatorIndex === -1 ? entryPath : entryPath.slice(separatorIndex + 1);
}

/** Indentation matches the previous nested-tree layout. */
function indentFor(depth: number, isFile: boolean): number {
  return 4 + depth * 12 + (isFile ? 12 : 0);
}

/** Keeps the vscode-icons glyphs at the 13px density the tabler icons had. */
const ENTRY_ICON_CLASS = "size-[13px] shrink-0";

interface TreeRowProps {
  readonly entry: ProjectEntry;
  readonly depth: number;
  readonly cwd: string;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (path: string) => void;
  readonly activeRelativePath: string | null;
  /**
   * Resolved once by the tree root: `useTheme` registers matchMedia and storage
   * listeners per call, so subscribing from every row would not scale.
   */
  readonly theme: "light" | "dark";
}

function TreeRow(props: TreeRowProps) {
  const { entry, depth, cwd, expanded, onToggle, activeRelativePath, theme } = props;
  const openFile = useEditorStore((state) => state.openFile);
  const name = basenameOf(entry.path);

  if (entry.kind === "directory") {
    const isExpanded = expanded.has(entry.path);
    return (
      <>
        <button
          type="button"
          className={
            "flex w-full items-center gap-1 truncate rounded-sm px-1 py-0.5 text-left text-xs " +
            "hover:bg-accent text-foreground/85" +
            (entry.ignored ? " opacity-60" : "")
          }
          title={entry.ignored ? `${entry.path} (gitignored)` : undefined}
          style={{ paddingLeft: indentFor(depth, false) }}
          onClick={() => onToggle(entry.path)}
        >
          {isExpanded ? (
            <IconChevronDown size={12} className="shrink-0 text-muted-foreground" />
          ) : (
            <IconChevronRight size={12} className="shrink-0 text-muted-foreground" />
          )}
          <VscodeEntryIcon
            pathValue={entry.path}
            kind="directory"
            theme={theme}
            className={ENTRY_ICON_CLASS}
          />
          <span className="truncate">{name}</span>
        </button>
        {isExpanded ? (
          <DirectoryChildren
            cwd={cwd}
            dirPath={entry.path}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            activeRelativePath={activeRelativePath}
            theme={theme}
          />
        ) : null}
      </>
    );
  }

  const isActive = activeRelativePath === entry.path;
  return (
    <button
      type="button"
      className={
        "flex w-full items-center gap-1 truncate rounded-sm px-1 py-0.5 text-left text-xs " +
        (isActive ? "bg-accent text-foreground" : "text-foreground/85 hover:bg-accent/60") +
        (entry.ignored ? " opacity-60" : "")
      }
      style={{ paddingLeft: indentFor(depth, true) }}
      onClick={() => {
        void openFile({ cwd, relativePath: entry.path });
      }}
      title={entry.ignored ? `${entry.path} (gitignored)` : entry.path}
    >
      <VscodeEntryIcon
        pathValue={entry.path}
        kind="file"
        theme={theme}
        className={ENTRY_ICON_CLASS}
      />
      <span className="truncate">{name}</span>
    </button>
  );
}

interface DirectoryChildrenProps {
  readonly cwd: string;
  readonly dirPath: string;
  readonly depth: number;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (path: string) => void;
  readonly activeRelativePath: string | null;
  readonly theme: "light" | "dark";
}

/**
 * Children of an expanded directory. Mounted only while the directory is open,
 * so collapsing simply stops querying — React Query keeps the cached listing
 * for an instant re-expand.
 */
function DirectoryChildren(props: DirectoryChildrenProps) {
  const { cwd, dirPath, depth, expanded, onToggle, activeRelativePath, theme } = props;
  const query = useQuery(projectListDirectoryQueryOptions({ cwd, dirPath }));

  if (query.isPending) {
    return (
      <div
        className="truncate py-0.5 text-xs text-muted-foreground"
        style={{ paddingLeft: indentFor(depth, true) }}
      >
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        className="truncate py-0.5 text-xs text-destructive"
        style={{ paddingLeft: indentFor(depth, true) }}
      >
        Failed to load folder: {(query.error as Error).message}
      </div>
    );
  }

  if (query.data.entries.length === 0) {
    return (
      <div
        className="truncate py-0.5 text-xs text-muted-foreground/70"
        style={{ paddingLeft: indentFor(depth, true) }}
      >
        Empty folder
      </div>
    );
  }

  return (
    <>
      {query.data.entries.map((entry) => (
        <TreeRow
          key={entry.path}
          entry={entry}
          depth={depth}
          cwd={cwd}
          expanded={expanded}
          onToggle={onToggle}
          activeRelativePath={activeRelativePath}
          theme={theme}
        />
      ))}
    </>
  );
}

export interface FileTreeProps {
  readonly cwd: string;
}

export function FileTree(props: FileTreeProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());
  const buffers = useEditorStore((state) => state.buffers);
  const activeTab = useEditorStore((state) => state.activeTab);
  const { resolvedTheme } = useTheme();

  const activeRelativePath = useMemo(() => {
    if (typeof activeTab === "string") return null;
    const buffer = buffers.find((b) => b.bufferId === activeTab.bufferId);
    return buffer && buffer.cwd === props.cwd && buffer.origin === "workspace"
      ? buffer.relativePath
      : null;
  }, [activeTab, buffers, props.cwd]);

  const rootQuery = useQuery(projectListDirectoryQueryOptions({ cwd: props.cwd, dirPath: "" }));

  const toggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  if (rootQuery.isPending) {
    return <div className="px-2 py-3 text-xs text-muted-foreground">Loading workspace files…</div>;
  }
  if (rootQuery.isError) {
    return (
      <div className="px-2 py-3 text-xs text-destructive">
        Failed to load file tree: {(rootQuery.error as Error).message}
      </div>
    );
  }
  if (rootQuery.data.entries.length === 0) {
    return <div className="px-2 py-3 text-xs text-muted-foreground">No files indexed yet.</div>;
  }

  return (
    <div className="flex flex-col gap-0 py-1">
      {rootQuery.data.entries.map((entry) => (
        <TreeRow
          key={entry.path}
          entry={entry}
          depth={0}
          cwd={props.cwd}
          expanded={expanded}
          onToggle={toggle}
          activeRelativePath={activeRelativePath}
          theme={resolvedTheme}
        />
      ))}
      {rootQuery.data.truncated ? (
        <div className="px-2 py-2 text-xs text-muted-foreground">
          Workspace index truncated. Some files may be missing.
        </div>
      ) : null}
    </div>
  );
}
